import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useI18n } from '../../context/I18nContext';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';
import AnimatedEyebrow from '../AnimatedEyebrow';

export default function HomeCtaSection({ sharedSurface = false }) {
  const { language, t } = useI18n();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.16,
        delayChildren: 0.12,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 28 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const content = (
    <div className={`relative ${sharedSurface ? 'pt-48 pb-24' : 'pt-[80px] pb-[60px]'}`}>
      <div className="container relative z-10 mx-auto p-8 md:px-6 lg:px-12">
        <div className={sharedSurface ? 'relative rounded-3xl bg-[var(--dash-main)]' : ''}>
          {sharedSurface ? (
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl">
              <div className="absolute inset-0 opacity-50 bg-[url('/bg/1.webp')] bg-no-repeat bg-[size:100%_100%]" />
            </div>
          ) : null}
          <motion.div
            key={language}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-120px' }}
            className={`relative flex flex-col items-center gap-16 lg:flex-row ${sharedSurface ? 'p-8 md:p-10 lg:p-12' : ''}`}
          >
            <div className="order-2 max-w-lg flex w-full flex-1 flex-col items-start lg:order-1">
              <motion.div variants={itemVariants} className="mb-6">
                <AnimatedEyebrow
                  surfaceClassName="bg-[var(--dash-black)]"
                  textClassName="text-[var(--dash-white)]"
                >
                  {t('home.cta.eyebrow')}
                </AnimatedEyebrow>
              </motion.div>

              <motion.h2
                variants={itemVariants}
                className="mb-2 text-4xl font-light uppercase !leading-[1.2] text-white sm:text-5xl"
              >
                {t('home.cta.titleLine1')}{' '}
                <span className="font-black text-white">{t('home.cta.titleLine2')}</span>
              </motion.h2>

              <motion.p variants={itemVariants} className="max-w-xl text-lg font-light leading-relaxed text-white/90 drop-shadow-sm">
                {t('home.cta.description')}
              </motion.p>

              <motion.div variants={itemVariants} className="mt-5">
                <Link
                  href="/contact"
                  className="group/cta relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-white px-8 py-4 text-sm font-bold text-[var(--dash-main)] transition-all active:scale-95 hover:pr-14 hover:bg-[var(--dash-blacker)] hover:text-white"
                >
                  <span className="relative z-10">{t('home.cta.button')}</span>
                  <ArrowRight className="absolute right-5 h-5 w-5 translate-x-8 opacity-0 transition-all group-hover/cta:translate-x-0 group-hover/cta:opacity-100" />
                </Link>
              </motion.div>
            </div>

            <motion.div
              variants={itemVariants}
              className="order-1 hidden w-full justify-center xl:flex lg:pointer-events-none lg:absolute lg:right-0 lg:justify-end"
            >
              <ImageOnlySkeletonLoader
                src="/2.webp"
                mode="native"
                alt={t('home.cta.imageAlt')}
                containerClassName="relative z-10 w-full max-w-[360px] sm:max-w-[520px] md:max-w-[620px] lg:right-[0%] lg:top-0 lg:w-[clamp(46rem,46vw,50rem)] lg:max-w-none"
                imgClassName="h-full w-full object-cover"
                disableSkeleton={true}
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );

  if (sharedSurface) {
    return content;
  }

  return (
    <section className="relative bg-[var(--dash-blacker)]">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[url('/bg/5.webp')] bg-no-repeat bg-cover bg-center" />
        <div className="absolute inset-0 z-0">
          <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_15%_25%,rgba(255,93,16,0.08)_0%,transparent_40%)]" />
        </div>
        <div className="absolute right-0 top-0 h-[420px] w-[420px] translate-x-1/4 -translate-y-1/2 rounded-full bg-[#ff5d10]/5 blur-[100px]" />
        <div className="absolute bottom-0 left-0 h-[340px] w-[340px] -translate-x-1/4 translate-y-1/2 rounded-full bg-blue-500/5 blur-[90px]" />
        <div
          aria-hidden="true"
          className="absolute inset-0 z-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(#ffffff 0.5px, transparent 0.5px)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>
      {content}
    </section>
  );
}
